import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const LS_KEY = 'anon_cart_v1';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const readLS = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) {
    return [];
  }
};

const writeLS = (items) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(items)); } catch (_e) { /* ignore */ }
};

const classifyFormuleItem = (dish) => {
  const id = dish?.dish_id || '';
  const price = dish?.price;
  if (!id.startsWith('pdj_')) return null;
  if (id.includes('entree')) return price === 8 ? 'entree' : null;
  if (id.includes('dessert')) return price === 6 ? 'dessert' : null;
  return price === 18 ? 'plat' : null;
};

const computePricing = (items) => {
  const subtotal = Math.round(items.reduce((sum, it) => sum + (it.dish?.price || 0) * it.quantity, 0) * 100) / 100;
  const counts = { entree: 0, plat: 0, dessert: 0 };
  items.forEach((it) => {
    const kind = classifyFormuleItem(it.dish);
    if (kind) counts[kind] += it.quantity;
  });
  let { entree: e, plat: p, dessert: d } = counts;
  const formules = [];
  const menus = Math.min(e, p, d); e -= menus; p -= menus; d -= menus;
  const ep = Math.min(e, p); e -= ep; p -= ep;
  const pd = Math.min(p, d);
  if (menus) formules.push({ label: 'Menu Complet (Entrée + Plat + Dessert) 26.–', count: menus, saving: 6 * menus });
  if (ep) formules.push({ label: 'Formule Entrée + Plat 22.–', count: ep, saving: 4 * ep });
  if (pd) formules.push({ label: 'Formule Plat + Dessert 22.–', count: pd, saving: 2 * pd });
  const discount = Math.round(formules.reduce((s, f) => s + f.saving, 0) * 100) / 100;
  return { subtotal, discount, formules, total: Math.round((subtotal - discount) * 100) / 100 };
};

export const CartProvider = ({ children }) => {
  const { axiosInstance, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const mergedRef = useRef(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      // Rebuild anon cart from localStorage (dish details already stored)
      const items = readLS();
      setCart({ items, ...computePricing(items) });
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get('/cart');
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAuthenticated]);

  // On authentication change, merge local cart into server cart then fetch
  useEffect(() => {
    const run = async () => {
      if (isAuthenticated && !mergedRef.current) {
        mergedRef.current = true;
        const anon = readLS();
        if (anon.length > 0) {
          for (const it of anon) {
            try {
              await axiosInstance.post('/cart/add', { dish_id: it.dish_id, quantity: it.quantity });
            } catch (_e) { /* skip individual failures */ }
          }
          try { localStorage.removeItem(LS_KEY); } catch (_e) { /* ignore */ }
        }
      }
      if (!isAuthenticated) {
        mergedRef.current = false;
      }
      await fetchCart();
    };
    run();
  }, [isAuthenticated, axiosInstance, fetchCart]);

  const addToCart = async (dishId, quantity = 1) => {
    if (isAuthenticated) {
      await axiosInstance.post('/cart/add', { dish_id: dishId, quantity });
      await fetchCart();
      return true;
    }
    // Anonymous: fetch dish details, then persist to localStorage
    const items = readLS();
    const existing = items.find((it) => it.dish_id === dishId);
    if (existing) {
      existing.quantity += quantity;
    } else {
      let dish = null;
      try {
        const resp = await axiosInstance.get(`/dishes/${dishId}`);
        dish = resp.data;
      } catch (_e) {
        throw new Error('Dish not found');
      }
      items.push({
        dish_id: dishId,
        quantity,
        dish,
        item_total: (dish?.price || 0) * quantity,
      });
    }
    // Recompute per-item totals
    items.forEach((it) => { it.item_total = (it.dish?.price || 0) * it.quantity; });
    writeLS(items);
    setCart({ items, ...computePricing(items) });
    return true;
  };

  const updateCartItem = async (dishId, quantity) => {
    if (isAuthenticated) {
      await axiosInstance.put('/cart/update', { dish_id: dishId, quantity });
      await fetchCart();
      return;
    }
    let items = readLS();
    if (quantity <= 0) {
      items = items.filter((it) => it.dish_id !== dishId);
    } else {
      const it = items.find((i) => i.dish_id === dishId);
      if (it) {
        it.quantity = quantity;
        it.item_total = (it.dish?.price || 0) * quantity;
      }
    }
    writeLS(items);
    setCart({ items, ...computePricing(items) });
  };

  const removeFromCart = async (dishId) => updateCartItem(dishId, 0);

  const clearCart = async () => {
    if (isAuthenticated) {
      try { await axiosInstance.delete('/cart/clear'); } catch (_e) { /* ignore */ }
    }
    try { localStorage.removeItem(LS_KEY); } catch (_e) { /* ignore */ }
    setCart({ items: [], total: 0 });
  };

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        fetchCart,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
